import { getConciergeGrants } from '@/hooks/use-concierge-permissions';
import { getLocalDateTimeString } from '@/utils/date-time';
import { CircleCircle } from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createRbcApi from '@rhg/libs/api/create-rbc-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { useActiveCircleId } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const InitiallyLoadedRecommendationsStorageKey =
    'initiallyLoadedRecommendations';

export function setHasInitiallyLoadedRecommendations() {
    sessionStorage.setItem(InitiallyLoadedRecommendationsStorageKey, 'true');
}
export function getHasInitiallyLoadedRecommendations() {
    return sessionStorage.getItem(InitiallyLoadedRecommendationsStorageKey);
}

export const circleRecommendationsQueryOptions = {
    queryKey: (circleId: number, includeAiCarePlan: boolean) => [
        queryKeys.recommendations,
        circleId,
        includeAiCarePlan,
    ],
    queryFn: async ({
        getAccessTokenSilently,
        circle,
        forceRefresh,
        includeAiCarePlan,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circle?: CircleCircle;
        forceRefresh?: boolean;
        includeAiCarePlan?: boolean;
    }) => {
        if (!circle) return {};
        const rbcApi = createRbcApi(getAccessTokenSilently);
        const circleAuthorization = circle?.authorizationJWT ?? '';
        const localDateTime = getLocalDateTimeString();

        const response = await rbcApi.getV1RecommendationsCircle({
            circleAuthorizationToken: circleAuthorization,
            recipientId: circle.recipient.id,
            localDateTime,
            forceRefresh,
            includeAiCarePlan,
        });
        return response.data.recommendations;
    },
    enabled: ({
        isAuthenticated,
        circle,
    }: {
        isAuthenticated: boolean;
        circle?: CircleCircle;
    }) =>
        !!(
            isAuthenticated &&
            circle &&
            circle.recipient.id > 0 &&
            circle.authorizationJWT
        ),
};

interface UseCircleRecommendationsProps {
    circleId: number;
}

export function useCircleRecommendations({
    circleId,
}: UseCircleRecommendationsProps) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle, hasPermission } = useCircle(circleId);
    const { canCreateConcierge: includeAiCarePlan } =
        getConciergeGrants(hasPermission);

    const { data, ...otherProps } = useQuery({
        queryKey: [
            ...circleRecommendationsQueryOptions.queryKey(
                circleId,
                includeAiCarePlan
            ),
        ],
        queryFn: async () => {
            const hasInitiallyLoadedRecommendations =
                getHasInitiallyLoadedRecommendations();
            const data = await circleRecommendationsQueryOptions.queryFn({
                getAccessTokenSilently,
                circle,
                forceRefresh: !hasInitiallyLoadedRecommendations,
                includeAiCarePlan,
            });

            setHasInitiallyLoadedRecommendations();
            return data ?? {};
        },
        enabled: circleRecommendationsQueryOptions.enabled({
            isAuthenticated,
            circle,
        }),
        staleTime: 15 * MINUTE_MS,
    });

    return {
        ...data,
        ...otherProps,
    };
}

export function useActiveCircleRecommendations() {
    const { activeCircleId } = useActiveCircleId();

    return useCircleRecommendations({ circleId: activeCircleId });
}
